import { Link } from '@inertiajs/react'

import { ArrowLeftIcon } from '@/components/Icons'
import { cn } from '@/lib/utils'

/**
 * Spec 11 · Admin page head — accent eyebrow, h1, optional actions on
 * the right. Kept as one component so every admin screen opens the
 * same way.
 */
export function AdminHead({
  eyebrow,
  title,
  sub,
  back,
  actions,
  className,
}: {
  eyebrow?: string
  title: string
  sub?: string
  back?: { href: string; label: string }
  actions?: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('mb-7', className)}>
      {back && (
        <Link
          href={back.href}
          className="mb-4 inline-flex items-center gap-2 text-[13px] font-medium text-ink-muted hover:text-signal-deep"
        >
          <ArrowLeftIcon size={16} />
          {back.label}
        </Link>
      )}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {eyebrow && (
            <p className="mb-2 text-[13px] font-medium leading-none text-signal-deep">
              {eyebrow}
            </p>
          )}
          <h1 className="sss-h1">{title}</h1>
          {sub && <p className="mt-2 text-[11px] text-ink-muted">{sub}</p>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-3">{actions}</div>}
      </div>
    </div>
  )
}

/** Surface panel with a hairline head — the admin's table container. */
export function AdminCard({
  title,
  action,
  children,
  className,
}: {
  title?: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('overflow-hidden rounded-md border border-hairline bg-surface', className)}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-4 border-b border-hairline px-[18px] py-3.5">
          {title && (
            <h2 className="text-[13px] font-medium leading-none">{title}</h2>
          )}
          {action}
        </div>
      )}
      {children}
    </div>
  )
}
